
import * as THREE from "../../../libs/three.js/build/three.module.js";
import {DigitizationTools} from "../ToolRegistry.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {createFeature} from "../Feature.js";
import {createDialogOverlay, fillDialog, pvpButton} from "../DialogHelpers.js";
import {polygonArea, polygonPerimeter} from "../geometry/measurementGeometry.js";
import {formatLength} from "./distanceTool.js";
import {FILL_COLOR, FILL_OPACITY, formatArea, averagePosition} from "./areaTool.js";

import {t, T} from "../i18n/index.js";
// "Farvet areal" (Specialiseret registrering) - samme opmålings-interaktion
// og label-kontrakt som "Mål areal" (venstreklik tilføjer punkt, højreklik
// afslutter, min. 3 punkter - FeatureVisual's egen minVertices for Polygon -
// ingen vertex-koordinat-labels, en segmentlængde-label pr. kant, ét
// resultat-label med navn/areal/omkreds/antal punkter - se areaTool.js's
// egen doc-kommentar), men når registreringen er afsluttet (højreklik)
// åbnes en popup hvor brugeren vælger farven på arealets fyld
// ("skraveringen") og skriver en fritekst-beskrivelse, som derefter indgår
// som en ekstra linje i resultat-labellen.
//
// customCapture, ikke deklarativ som Mål areal: DigitizationModule.
// startInsertion()'s generiske flow opretter feature'en og genstarter
// værktøjet med det samme, med ingen indbygget plads til et efterfølgende
// popup-trin. Fase 1 herunder er derfor en selvstændig, håndskrevet
// opmålings-løkke - men bevidst IDENTISK i adfærd med Mål areals egen
// (samme suppressVertexLabels/segmentLabelFormat/fill/min. 3 punkter-krav),
// ikke en ny/anden slags registrering. Fase 2 (popup) følger samme mønster
// som Cirkel/Rektangel: feature'en oprettes FØR dialogen åbner (så resultat-
// labellen allerede lever og opdateres live imens brugeren vælger
// farve/beskrivelse), og fjernes igen hvis dialogen annulleres.
export const LabeledAreaTool = {
	id: "digitize_labeled_area",
	name: T("Farvet areal"),
	category: "specialized",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 70,
	icon: "measure_area.svg",
	resultGeometryType: "Polygon",
	tooltip: T("Digitalisér et areal og angiv bagefter farve og beskrivelse - beskrivelsen indgår i resultat-labellen."),
	instructions: T("Venstreklik for at placere områdets punkter. Højreklik for at afslutte og lukke området."),
	fillColor: FILL_COLOR,
	fillOpacity: FILL_OPACITY,
	customCapture(dm, tool){ runLabeledAreaCapture(dm, tool); },
	// Restore extension point (see DigitizationModule._buildLoadedFeature): the finished labeled area keeps its segment labels, hidden
	// vertex labels, result label (the description is already on the visual) and the fill colour the user picked (domainData.appearance).
	decorateLoaded(visual, feature){
		configureLabeledAreaVisual(visual);
		visual.enableSummaryLabel(labeledAreaSummaryLines, (raw) => averagePosition(raw));
		const appearance = feature && feature.domainData && feature.domainData.appearance;
		if(appearance && Number.isFinite(appearance.fillColor)) visual.enableFill(appearance.fillColor, visual.fillOpacity);
	},
};

DigitizationTools.register(LabeledAreaTool);

// The measurement look shared by the capture flow and the restore hook: no vertex coordinate labels, one length label per edge.
function configureLabeledAreaVisual(visual){
	visual.setVertexLabelsSuppressed(true);
	visual.enableSegmentLabels((a, b) => formatLength(a.distanceTo(b)));
}

// Eksporteret for Level 1-test - ren funktion af (raw, {name, description}),
// ingen DOM/THREE. `visual.description` er den ALMINDELIGE, allerede
// eksisterende feature-beskrivelse (samme felt FeaturePanel.js's eget
// beskrivelse-felt redigerer og feature.properties.description
// eksporterer) - ikke et nyt, parallelt datafelt.
export function labeledAreaSummaryLines(raw, visual){
	if(raw.length < 3) return null;
	const lines = [visual.name];
	if(visual.description) lines.push(t("Beskrivelse: {0}", visual.description));
	lines.push(t("Areal: {0}", formatArea(polygonArea(raw))));
	lines.push(t("Omkreds: {0}", formatLength(polygonPerimeter(raw))));
	lines.push(t("Antal punkter: {0}", raw.length));
	return lines;
}

// Fase 1: opmåling - se denne fils egen doc-kommentar for hvorfor dette er
// en selvstændig løkke frem for den generiske deklarative flow.
function runLabeledAreaCapture(dm, tool){
	const domElement = dm.viewer.renderer.domElement;

	dm._showInstruction(tool.name, tool.instructions);

	const visual = new FeatureVisual("Polygon");
	visual.setInfo(tool.name, "");
	configureLabeledAreaVisual(visual);
	if(tool.fillColor) visual.enableFill(tool.fillColor, tool.fillOpacity);
	dm.scene.add(visual);

	let finished = false;

	const cleanup = () => {
		domElement.removeEventListener('mouseup', onMouseUp, false);
		dm.viewer.removeEventListener('cancel_insertions', onCancel);
		dm.clearRubberBand();
	};

	const onCancel = () => {
		if(finished) return;
		finished = true;
		cleanup();
		dm.scene.remove(visual);
	};

	const finish = () => {
		// samme minVertices-krav (3) som Mål areal - et for tidligt højreklik
		// er blot et no-op, ligesom den generiske flows egen "for få punkter"-håndtering
		if(finished || visual.points.length < visual.minVertices) return;
		finished = true;
		cleanup();
		runLabeledAreaPopupPhase(dm, tool, visual);
	};

	const onMouseUp = (e) => {
		if(finished || dm.isPaused() || dm._consumeCopyClickGuard()) return;

		if(e.button === THREE.MOUSE.LEFT){
			const location = dm._pickPlacementPosition(e);
			if(location){
				visual.appendAtEnd(location);
				dm._markOperationStarted();
				dm.syncRubberBandToVisual(visual);
			}
		}else if(e.button === THREE.MOUSE.RIGHT){
			finish();
		}
	};

	domElement.addEventListener('mouseup', onMouseUp, false);
	dm.viewer.addEventListener('cancel_insertions', onCancel);
}

// Fase 2: registrér feature'en (samme rækkefølge som Cirkel/Rektangel - se
// deres egne doc-kommentarer), aktivér resultat-labellen, åbn derefter
// farve/beskrivelse-popup'en. Ingen auto-genstart efter dialogen (samme
// enkeltskuds-mønster som Cirkel/Rektangel - den mest beslægtede
// præcedens, da de også har et popup-trin efter selve opmålingen).
function runLabeledAreaPopupPhase(dm, tool, visual){
	const feature = createFeature({
		geometryType: "Polygon",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.name,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
	});
	visual.setInfo(feature.properties.name, feature.properties.description);
	dm.registerNewFeature(feature, visual);

	visual.enableSummaryLabel(labeledAreaSummaryLines, (raw) => averagePosition(raw));

	openLabeledAreaDialog(dm, visual, tool).then((committed) => {
		if(!committed) dm.removeFeature(feature.id); // "Cancel efterlader ingen permanent feature" - samme regel som Cirkel/Rektangel
		dm.stopInsertion();
	});
}

// Farve/beskrivelse-dialog - samme mørke modal-konvention som Cirkel/
// Rektangel (createDialogOverlay). Farven anvendes live direkte på fyldet
// (visual.enableFill() genopbygger fra bunden hver gang - billigt for et
// enkelt areal). Beskrivelsen går gennem den EKSISTERENDE
// featureManager.updateProperties() + visual.setInfo() - samme to linjer
// FeaturePanel.js's eget beskrivelse-felt allerede bruger (se dens egen
// input-listener) - så beskrivelsen er en almindelig, eksporterbar
// feature.properties.description som for enhver anden feature, ikke et nyt
// parallelt datafelt, og setInfo() genopfrisker automatisk resultat-
// labellen (samme mekanisme som FeatureVisual.js's enableSummaryLabel()
// dokumenterer).
function openLabeledAreaDialog(dm, visual, tool){
	return new Promise((resolve) => {
		const feature = dm.getFeature(visual);
		if(!feature){ resolve(false); return; }

		const originalFillColor = visual.fillColor;
		const originalDescription = visual.description || "";
		let done = false;

		const {overlay, box} = createDialogOverlay(dm, 300);
		const colorHex = `#${originalFillColor.toString(16).padStart(6, "0")}`;
		fillDialog(box, {
			title: `${tool.name}`,
			description: t(`Vælg farve på arealets skravering, og skriv en beskrivelse - den vises i resultat-labellen.`),
			body: `<label class="pvp-label">${t("Farve")}</label>
				<input class="pvp-input pvp-input--color" type="color" value="${colorHex}" data-role="color"
				 />
				<label class="pvp-label">${t("Beskrivelse")}</label>
				<textarea class="pvp-input" data-role="description" rows="3"
				></textarea>`,
			actions: pvpButton('cancel', t("Annuller")) + pvpButton('commit', t("Godkend"), 'primary'),
		});

		const colorInput = box.querySelector('[data-role="color"]');
		const descInput = box.querySelector('[data-role="description"]');
		descInput.value = originalDescription;
		descInput.focus();

		colorInput.addEventListener('input', () => {
			const hex = parseInt(colorInput.value.slice(1), 16);
			visual.enableFill(hex, visual.fillOpacity);
			dm.featureManager.setDomainData(feature.id, "appearance", {fillColor: hex});
		});
		descInput.addEventListener('input', () => {
			dm.featureManager.updateProperties(feature.id, {description: descInput.value});
			visual.setInfo(visual.name, descInput.value);
		});

		const cleanup = () => {
			dm.viewer.removeEventListener('cancel_insertions', onCancelEvent);
			document.body.removeChild(overlay);
		};

		const finish = (committed) => {
			if(done) return;
			done = true;
			cleanup();
			if(committed){
				dm.clearMultiSelection();
			}else{
				visual.enableFill(originalFillColor, visual.fillOpacity);
				dm.featureManager.updateProperties(feature.id, {description: originalDescription});
				visual.setInfo(visual.name, originalDescription);
				dm.featureManager.clearDomainData(feature.id, "appearance");
			}
			resolve(committed);
		};

		box.querySelector('[data-action="commit"]').addEventListener('click', () => finish(true));
		box.querySelector('[data-action="cancel"]').addEventListener('click', () => finish(false));
		const onCancelEvent = () => finish(false);
		box.addEventListener('keydown', (e) => {
			// intet Enter-genvej her (i modsætning til Cirkel/Rektangel) - en
			// flerlinjet beskrivelse har brug for Enter til linjeskift
			if(e.key === 'Escape'){ e.stopPropagation(); finish(false); }
		});

		dm.viewer.addEventListener('cancel_insertions', onCancelEvent);
	});
}
